import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  Save,
  Banknote,
  Power,
  MessageSquareWarning,
  Wallet,
  ShieldCheck,
  Star,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const PROVIDER_THEME = {
  hrpay:    { tone: "#10b981", short: "HRpay" },
  cowpay:   { tone: "#f59e0b", short: "Cowpay" },
  wpay:     { tone: "#3b82f6", short: "WPay" },
  starpago: { tone: "#a855f7", short: "StarPago" },
};

const METHOD_LABELS = { bkash: "bKash", nagad: "Nagad", rocket: "Rocket" };
const METHOD_COLORS = { bkash: "#E2136B", nagad: "#EE2C28", rocket: "#8E1B8F" };
const ALL_METHODS = ["bkash", "nagad", "rocket"];

export default function ConfigAgentBdtDeposit() {
  const [cfg, setCfg] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/agent-bdt-deposit");
      setCfg({
        enabled:             data?.enabled !== false,
        min_amount:          Number(data?.min_amount ?? 500),
        max_amount:          Number(data?.max_amount ?? 100000),
        notice:              data?.notice || "",
        enabled_gateway_ids: Array.isArray(data?.enabled_gateway_ids) ? data.enabled_gateway_ids : [],
        enabled_methods:     Array.isArray(data?.enabled_methods) && data.enabled_methods.length
          ? data.enabled_methods
          : ALL_METHODS,
      });
      setGateways(Array.isArray(data?.available_gateways) ? data.available_gateways : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    if (!cfg) return;
    if (cfg.min_amount < 0 || cfg.max_amount < 0) {
      toast.error("Amounts cannot be negative");
      return;
    }
    if (cfg.max_amount > 0 && cfg.min_amount > cfg.max_amount) {
      toast.error("Min amount cannot exceed max amount");
      return;
    }
    if (!cfg.enabled_methods.length) {
      toast.error("At least one wallet method (bKash/Nagad/Rocket) must be enabled");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/agent-bdt-deposit", cfg);
      setCfg((prev) => ({
        ...prev,
        enabled:             data?.enabled !== false,
        min_amount:          Number(data?.min_amount ?? prev.min_amount),
        max_amount:          Number(data?.max_amount ?? prev.max_amount),
        notice:              data?.notice ?? prev.notice,
        enabled_gateway_ids: Array.isArray(data?.enabled_gateway_ids) ? data.enabled_gateway_ids : prev.enabled_gateway_ids,
        enabled_methods:     Array.isArray(data?.enabled_methods) ? data.enabled_methods : prev.enabled_methods,
      }));
      toast.success("Agent BDT deposit settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleGateway = (id) => {
    setCfg((c) => {
      const set = new Set(c.enabled_gateway_ids);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...c, enabled_gateway_ids: [...set] };
    });
  };

  const toggleMethod = (m) => {
    setCfg((c) => {
      const set = new Set(c.enabled_methods);
      if (set.has(m)) set.delete(m); else set.add(m);
      return { ...c, enabled_methods: [...set] };
    });
  };

  const allowAllGateways = () => {
    setCfg((c) => ({ ...c, enabled_gateway_ids: [] }));
  };

  if (loading || !cfg) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500"
        data-testid="cfg-agent-bdt-loading"
      >
        Loading…
      </div>
    );
  }

  const gatewayAllowAll = cfg.enabled_gateway_ids.length === 0;

  return (
    <div data-testid="cfg-agent-bdt-deposit-page">
      <PageHeader
        title="Configuration · Agent BDT Deposit"
        subtitle="Full control over the agent BDT top-up flow — enable/disable the feature, set amount limits, pick which payment gateways and wallets agents can use, and write a custom notice."
        actions={
          <a
            href="/root/admin/agents/bdt-deposits"
            className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
            data-testid="cfg-agent-bdt-go-list"
          >
            View Submissions
          </a>
        }
      />

      <form
        onSubmit={save}
        className="bg-white border border-gray-200 rounded-sm p-6 max-w-4xl space-y-6"
        data-testid="cfg-agent-bdt-form"
      >
        {/* Master toggle */}
        <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-sm bg-gray-50">
          <div className="h-10 w-10 rounded-sm bg-emerald-500/10 text-emerald-600 inline-flex items-center justify-center shrink-0">
            <Power size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-gray-900">Enable Agent BDT Deposit</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  When ON, agents see the BDT Deposit page in their panel and can top up via gateway-hosted checkout. Balance credits automatically on webhook confirmation.
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!cfg.enabled}
                  onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))}
                  className="sr-only peer"
                  data-testid="cfg-agent-bdt-enabled"
                />
                <span className="relative w-11 h-6 bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
              </label>
            </div>
          </div>
        </div>

        {/* Min / Max */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
              <Banknote size={13} /> Min amount (BDT)
            </label>
            <input
              type="number"
              min={0}
              step={1}
              value={cfg.min_amount}
              onChange={(e) => setCfg((c) => ({ ...c, min_amount: Number(e.target.value) || 0 }))}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
              data-testid="cfg-agent-bdt-min"
            />
            <div className="text-[11px] text-gray-500 mt-1">Smallest BDT amount an agent can submit in one go.</div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
              <Banknote size={13} /> Max amount (BDT)
            </label>
            <input
              type="number"
              min={0}
              step={1}
              value={cfg.max_amount}
              onChange={(e) => setCfg((c) => ({ ...c, max_amount: Number(e.target.value) || 0 }))}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
              data-testid="cfg-agent-bdt-max"
            />
            <div className="text-[11px] text-gray-500 mt-1">Set 0 for no upper cap.</div>
          </div>
        </div>

        {/* ---------- Allowed wallet methods ---------- */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
              <Wallet size={13} /> Allowed wallets
            </label>
            <span className="text-[11px] text-gray-400">{cfg.enabled_methods.length}/3 active</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {ALL_METHODS.map((m) => {
              const active = cfg.enabled_methods.includes(m);
              const color = METHOD_COLORS[m];
              return (
                <button
                  type="button"
                  key={m}
                  onClick={() => toggleMethod(m)}
                  data-testid={`cfg-agent-bdt-method-${m}`}
                  className={`h-12 px-3 rounded-sm border text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    active
                      ? "border-transparent text-white"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                  style={
                    active
                      ? { background: color, boxShadow: `0 6px 14px -8px ${color}99` }
                      : undefined
                  }
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: active ? "#fff" : color }}
                  />
                  {METHOD_LABELS[m]}
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-gray-500 mt-1.5">
            Wallets shown in Step-1 of the agent&apos;s BDT deposit form. Each gateway only accepts methods it actually supports — admin toggle is an additional filter on top.
          </div>
        </div>

        {/* ---------- Allowed gateways ---------- */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
              <ShieldCheck size={13} /> Allowed payment gateways
            </label>
            <button
              type="button"
              onClick={allowAllGateways}
              className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold"
              data-testid="cfg-agent-bdt-allow-all-gw"
            >
              Allow all (clear list)
            </button>
          </div>

          {gateways.length === 0 ? (
            <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-sm px-3 py-2 text-[12px] inline-flex items-center gap-2">
              <Info size={13} />
              No enabled gateways found. Enable HRpay / Cowpay / WPay / StarPago / TarsPay in Payment Gateways first.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {gateways.map((g) => {
                const t = PROVIDER_THEME[g.provider] || { tone: "#10b981", short: g.provider };
                const active = gatewayAllowAll || cfg.enabled_gateway_ids.includes(g.id);
                return (
                  <label
                    key={g.id}
                    className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-all ${
                      active ? "border-gray-300 bg-white" : "border-gray-200 bg-gray-50 opacity-70 hover:opacity-100"
                    }`}
                    data-testid={`cfg-agent-bdt-gateway-${g.provider}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleGateway(g.id)}
                      className="mt-1 h-4 w-4 accent-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span
                          className="h-5 px-1.5 rounded text-[10px] font-bold uppercase tracking-wider inline-flex items-center"
                          style={{ background: `${t.tone}1f`, color: t.tone }}
                        >
                          {t.short}
                        </span>
                        {g.recommended && (
                          <span className="inline-flex items-center gap-0.5 h-5 px-1.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600">
                            <Star size={9} /> Recommended
                          </span>
                        )}
                      </div>
                      <div className="text-[13px] font-semibold text-gray-900 truncate">{g.label || t.short}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        Supports {(g.methods || []).map((m) => METHOD_LABELS[m] || m).join(" · ") || "—"}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <div className="text-[11px] text-gray-500 mt-1.5">
            Tick the gateways agents are allowed to deposit through. Untick to hide a gateway from the Agent Panel without disabling it site-wide. Leave all unticked = use ALL enabled gateways (default).
          </div>
        </div>

        {/* Notice */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 inline-flex items-center gap-1.5">
            <MessageSquareWarning size={13} /> Notice for agents (shown in the deposit form)
          </label>
          <textarea
            rows={4}
            value={cfg.notice}
            maxLength={500}
            onChange={(e) => setCfg((c) => ({ ...c, notice: e.target.value }))}
            placeholder="Write any important instructions, warnings or office hours…"
            className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm leading-relaxed focus:outline-none focus:border-emerald-500"
            data-testid="cfg-agent-bdt-notice"
          />
          <div className="text-[11px] text-gray-500 mt-1">{cfg.notice.length}/500 characters.</div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="text-[11px] text-gray-500">
            Payment gateway credentials are configured in <b>Payment Gateways</b>. This page only controls visibility & limits for agents.
          </div>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-agent-bdt-save"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
